/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback} from 'react';
import {allExpanded, defaultStyles, JsonView} from 'react-json-view-lite';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import TertiaryButton from '@ion/dls/src/components/buttons/TertiaryButton';
import IonDialog from '@ion/dls/src/components/dialogs/IonDialog';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import 'react-json-view-lite/dist/index.css';

interface SentinelRawMessageProps {
  open?: boolean;
  data?: GridResult;
  handleRawMessage?: (rawMessage?: boolean) => void;
};

const useStyles = makeStyles(() => {
  return {
    rawMessageContainer : {
      whiteSpace : 'pre-wrap'
    }
  };
});

/**
 * Displays the raw event message details for a Sentinel event in a modal dialog.
 * Useful for debugging or viewing the underlying JSON/app message structure of a
 * Sentinel event.
 *
 * ```
 * 
 * <SentinelRawMessage
 *   data={eventData}
 *   handleRawMessage={setShowRawMessage}
 *   open={showRawMessage}
 * />
 * 
 * ```
 *
 */
const SentinelRawMessage = ({
  data,
  handleRawMessage,
  open = false
}: SentinelRawMessageProps) => {
  const classes = useStyles();

  const onCloseDialog = useCallback(() => {
    handleRawMessage?.(false);
  }, [handleRawMessage]);

  return (
    <IonDialog isMaxSize
      open={open}
      onClose={onCloseDialog}
      actions={
        <TertiaryButton onClick={onCloseDialog}>
          {translate('common:editors.shared.close')}
        </TertiaryButton>
      }
      title={translate('admin-devices:view.sentinel.title.eventMessage')}>
      <div className={classes.rawMessageContainer}>
        <form>
          <pre><JsonView data={data?.appMessage || ''} shouldExpandNode={allExpanded} style={defaultStyles} /></pre>
        </form>
      </div>
    </IonDialog>
  );
};

export default React.memo(SentinelRawMessage);
